import { useState, useRef } from 'react';
import { AnchoredPanel } from '../../components/AnchoredPanel.tsx';
import { Icons, cx } from '../../components/ui.tsx';
import { useDocumentCreation } from './useDocumentCreation.ts';

/**
 * "New document", with the templates behind it.
 *
 * The Drive's toolbar uses the single New menu instead; this split button
 * remains for the dashboard, where there is no toolbar to hang a menu on.
 * Both call the same useDocumentCreation, so the two cannot drift.
 */
export function NewDocumentButton({ folderId }: { folderId: string | null }) {
  const [open, setOpen] = useState(false);
  // See AnchoredPanel: the menu is placed against the window, not this corner.
  const group = useRef<HTMLDivElement | null>(null);
  const { templates, createFrom } = useDocumentCreation(folderId);

  function pickLocation(template: { id: string; name: string }) {
    setOpen(false);
    createFrom(template);
  }

  return (
    <div className="relative" ref={group}>
      <div className="flex">
        <button
          type="button"
          onClick={() => pickLocation({ id: 'blank', name: 'Untitled document' })}
          className="inline-flex h-9 items-center gap-1.5 rounded-l-md border border-border-strong bg-surface px-3 text-body-sm font-medium text-ink transition-fast hover:bg-sunken"
        >
          <Icons.Plus />
          New document
        </button>
        <button
          type="button"
          aria-label="Document templates"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className={cx(
            'inline-flex h-9 w-8 items-center justify-center rounded-r-md border border-l-0 border-border-strong text-muted transition-fast hover:bg-sunken',
            open && 'bg-sunken',
          )}
        >
          <span className="text-[10px]" aria-hidden>
            ▾
          </span>
        </button>
      </div>

      {open ? (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <AnchoredPanel trigger={group} width={288}>
            <p className="px-3 py-1.5 text-micro font-semibold uppercase tracking-wide text-muted">
              Start from
            </p>
            {(templates.data ?? []).map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => pickLocation(template)}
                className="block w-full px-3 py-2 text-left transition-fast hover:bg-sunken"
              >
                <span className="block text-body-sm text-ink">{template.name}</span>
                <span className="block text-micro text-muted">{template.description}</span>
              </button>
            ))}
            {templates.isLoading ? (
              <p className="px-3 py-2 text-body-sm text-muted">Loading templates…</p>
            ) : null}
            <div className="my-1 h-px bg-[var(--border)]" />
            <p className="px-3 py-1.5 text-micro text-muted">
              To open a Word file, upload it — a .docx becomes an editable document by itself.
            </p>
          </AnchoredPanel>
        </>
      ) : null}
    </div>
  );
}
